import { useEffect } from 'react';

const BASE_URL = 'https://mifelife.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-cover.png`;

const MARKER = 'data-mife-seo';

function setOrCreate(selector, attrs, parent = document.head) {
  let el = parent.querySelector(selector + `[${MARKER}]`);
  if (!el) {
    el = document.createElement(selector.split('[')[0]);
    el.setAttribute(MARKER, '1');
    parent.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined) continue;
    el.setAttribute(k, v);
  }
  return el;
}

function setMeta(name, content, attr = 'name') {
  const sel = `meta[${attr}="${name}"]`;
  // 1) update any pre-existing static meta (so Lighthouse / SEO scrapers see one consistent value)
  const existing = document.head.querySelector(sel);
  if (existing) {
    existing.setAttribute('content', content);
    return;
  }
  // 2) otherwise inject a managed one
  setOrCreate(sel, { [attr]: name, content });
}

function setCanonical(href) {
  // Prefer updating the existing static canonical to avoid duplicates that confuse search engines.
  const existing = document.head.querySelector('link[rel="canonical"]');
  if (existing) {
    existing.setAttribute('href', href);
    return;
  }
  setOrCreate('link[rel="canonical"]', { rel: 'canonical', href });
}

function setHreflang(lang, href) {
  // No-op: single-locale (TR) site — hreflang alternates intentionally not emitted.
}
void setHreflang;

function setJsonLd(schemas) {
  // Remove only managed JSON-LD scripts; leave global ones in index.html alone.
  document.head.querySelectorAll(`script[type="application/ld+json"][${MARKER}]`).forEach((el) => el.parentNode.removeChild(el));
  if (!schemas || !schemas.length) return;
  schemas.forEach((schema) => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute(MARKER, '1');
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
  });
}

/**
 * SEO component – directly manages canonical, hreflang, meta and JSON-LD on the document head.
 * Avoids react-helmet quirks with React 18 / StrictMode.
 */
export function SEO({
  title = 'MifeLife — Understand Why You\'re Stuck. AI Strategic Intelligence.',
  description = 'AI-powered strategic intelligence platform. Diagnoses growth bottlenecks, operational weaknesses and revenue leakage — then builds an actionable 30/60/90 day roadmap.',
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = null,
}) {
  const lang = 'tr'; // single-locale site — never emit EN/DE locale signals

  useEffect(() => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const canonicalUrl = `${BASE_URL}${cleanPath}`;
    const ogLocale = 'tr_TR';

    document.title = title;
    document.documentElement.setAttribute('lang', lang);

    setMeta('description', description);
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');

    setCanonical(canonicalUrl);

    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', image, 'property');
    setMeta('og:locale', ogLocale, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:site_name', 'MifeLife', 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);

    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    setJsonLd(schemas);

    return () => {
      // Only remove our managed JSON-LD; leave canonical/meta because they will be overwritten on next route.
      document.head.querySelectorAll(`script[type="application/ld+json"][${MARKER}]`).forEach((el) => el.parentNode.removeChild(el));
    };
  }, [title, description, path, image, noindex, lang, JSON.stringify(jsonLd)]);

  return null;
}

export default SEO;
